import { ref, Ref } from 'vue'

// Types
import { Locale } from '@/types'

// Configuration for endpoints
import endpoints from '@/config/endpoints'

// CSE
import CSEService from '@/services/CSEService'

// Reactive flag to determine if a refetch is needed
const needToRefetch: Ref<boolean> = ref(false)

// Custom composable function for managing header and footer
export const useHeaderFooter = (): {
  injectHeaderAndFooter: (
    menuDomain: string,
    locale: Locale,
    company?: string
  ) => void
  removeHeaderAndFooter: () => void
  needToRefetch: boolean
} => {
    // Function to generate header and footer URLs
  function getHeaderFooterUrl(
    menuDomain: string,
    locale: Locale,
    company?: string
  ): { headerUrl: string; footerUrl: string } {
    const cseService = new CSEService()
    const isCSE = cseService.isCSE

    // Clean the domain (remove protocol and trailing slashes)
    const cleanDomain = menuDomain.replace(/^https?:\/\//, '').replace(/\/+$/, '')

    // Generate header URL
    let headerUrl = isCSE
      ? `${endpoints.menu}/${cleanDomain}/${company}/header.html`
      : `${endpoints.menu}/${cleanDomain}/header.html`

    // Special case for specific locale with no company
    if (locale === 'fr-FR-CSE' && !company) {
      headerUrl = `${endpoints.menu}/${cleanDomain}/default-header.html`
      needToRefetch.value = true
    } else {
      needToRefetch.value = false
    }

    // Generate footer URL
    const footerUrl = isCSE
      ? `${endpoints.menu}/${cleanDomain}/${company}/footer.html`
      : `${endpoints.menu}/${cleanDomain}/footer.html`



    return { headerUrl, footerUrl }
}

  // Function to inject header and footer elements
// Function to inject header and footer elements
function injectHeaderAndFooter(menuDomain: string, locale: Locale, company?: string) {
  const { headerUrl, footerUrl } = getHeaderFooterUrl(menuDomain, locale, company);

  const header_wrapper = document.querySelector('#header-wrapper') as HTMLDivElement;
  const footer_wrapper = document.querySelector('#footer-wrapper') as HTMLDivElement;

    // Hide body initially
    document.body.classList.add('hidden');

    let headerLoaded = false;
    let footerLoaded = false;

    function checkAndShowBody() {
      if (headerLoaded && footerLoaded) {
        document.body.classList.remove('hidden');
      }
    }

    async function loadHeader() {
    try {
      const res = await fetchHeaderOrFooter(headerUrl, 'header');
      if (!res.elementHtml) throw new Error(`No header for domain: ${menuDomain}`);

      const { elementHtml, linksAndScriptsHtml, bannerElementHtml } = res;
      header_wrapper.insertAdjacentHTML('afterbegin', elementHtml);
      header_wrapper.insertAdjacentHTML('beforeend', linksAndScriptsHtml);
      header_wrapper.insertAdjacentHTML('afterbegin', bannerElementHtml);
      executeScripts('header-wrapper');
    } catch (error) {
      console.error('Error loading header:', error);
    } finally {
      headerLoaded = true;
      checkAndShowBody(); // Ensure body is shown even if header fails
    }
  }

  async function loadFooter() {
    try {
      const res = await fetchHeaderOrFooter(footerUrl, 'footer');
      if (!res.elementHtml) throw new Error(`No footer for domain: ${menuDomain}`);

      const { elementHtml } = res;
      footer_wrapper.insertAdjacentHTML('afterbegin', elementHtml);
      executeScripts('footer-wrapper');
    } catch (error) {
      console.error('Error loading footer:', error);
    } finally {
      footerLoaded = true;
      checkAndShowBody(); // Ensure body is shown even if footer fails
    }
  }

  // Start loading header and footer
  loadHeader();
  loadFooter();
}


  // Function to remove the current header and footer elements
  function removeHeaderAndFooter() {
    const header_wrapper = document.querySelector(
      '#header-wrapper'
    ) as HTMLDivElement
    const footer_wrapper = document.querySelector(
      '#footer-wrapper'
    ) as HTMLDivElement

    if (header_wrapper) {
      header_wrapper.innerHTML = '' // Clear header content
    }
    if (footer_wrapper) {
      footer_wrapper.innerHTML = '' // Clear footer content
    }
  }

  // Function to execute inline and external scripts in a container
  function executeScripts(containerId: string) {
    const container = document.getElementById(containerId) as HTMLDivElement
    const scripts = container.getElementsByTagName('script')

    for (let i = 0; i < scripts.length; i++) {
      const oldScript = scripts[i]
      const newScript = document.createElement('script')
      newScript.text = oldScript.text

      if (oldScript.src) {
        newScript.src = oldScript.src
      }

      for (let j = 0; j < oldScript.attributes.length; j++) {
        const attrName = oldScript.attributes[j].nodeName
        newScript.setAttribute(
          attrName,
          oldScript.getAttribute(attrName) as string
        )
      }

      if (oldScript) {
        oldScript.parentNode?.replaceChild(newScript, oldScript)
      }
    }
  }

  // Function to fetch header or footer HTML from a URL
  async function fetchHeaderOrFooter(url: string, tag: string) {
    const response = await fetch(url)
    const text = await response.text()
    const parser = new DOMParser()
    const doc = parser.parseFromString(text, 'text/html')

    const element = doc.querySelector(tag)
    const bannerElement = doc.querySelector('#header-banner')
    const elementHtml = element ? element.outerHTML : ''
    const bannerElementHtml = bannerElement ? bannerElement.outerHTML : ''

    const linksAndScripts = doc.querySelectorAll(
        'script[src*="header.js"], script[src*="participants-pop-up.js"], ' +
        'script[data-init-popup], script[src*="footer.js"]'
    )

    let linksAndScriptsHtml = ''
    linksAndScripts.forEach(ls => {
      linksAndScriptsHtml += ls.outerHTML
    })

    return { elementHtml, bannerElementHtml, linksAndScriptsHtml }
  }

  return {
    injectHeaderAndFooter,
    removeHeaderAndFooter,
    needToRefetch: needToRefetch.value,
  }
}
